import React, { useEffect, useMemo, useState } from 'react';
import {
  Bot, Plus, Copy, Archive, CheckCircle2, Trash2, Pencil, Sparkles, Star, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { aiAssistantAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { EmptyState, CardSkeleton } from './common/States';
import { confirmToast } from '../lib/confirmToast';
import { getErrorMessage } from '../lib/utils';

const ALL_ROLES = ['workspace_admin', 'hr_admin', 'hr_manager', 'manager', 'employee'];
const ALL_MODULES = ['leave', 'attendance', 'policies', 'holidays', 'helpdesk', 'documents', 'payroll', 'performance'];
const STATUS_TONE = { active: 'success', inactive: 'warning', archived: 'secondary' };

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-muted-foreground hover:border-primary/40'}`}>
    {children}
  </button>
);

const blank = () => ({
  name: '', description: '', theme_color: '#3b4fb8', avatar_url: '',
  welcome_message: '', system_prompt: '', provider: '', model: '',
  temperature: 0.4, max_tokens: 1024,
  allowed_roles: [...ALL_ROLES], allowed_modules: [...ALL_MODULES], allowed_tools: [], is_default: false,
});

export default function AIAgentBuilder() {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [providers, setProviders] = useState([]);
  const [editing, setEditing] = useState(null); // agent object or blank()
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [a, p] = await Promise.all([
        aiAssistantAPI.listAgents({ include_all: true }),
        aiAssistantAPI.getProviders().catch(() => ({ data: { providers: [] } })),
      ]);
      setAgents(a.data?.data || []);
      setProviders(p.data?.providers || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load agents'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const activeProvider = useMemo(() => providers.find((p) => p.id === editing?.provider), [providers, editing]);
  const set = (patch) => setEditing((e) => ({ ...e, ...patch }));
  const toggleIn = (key, val) => set({ [key]: (editing[key] || []).includes(val) ? editing[key].filter((x) => x !== val) : [...(editing[key] || []), val] });

  const save = async () => {
    if (!editing.name?.trim()) { toast.error('Agent name is required'); return; }
    setSaving(true);
    try {
      if (editing.id) await aiAssistantAPI.updateAgent(editing.id, editing);
      else await aiAssistantAPI.createAgent(editing);
      toast.success(editing.id ? 'Agent updated' : 'Agent created');
      setEditing(null); load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save agent'));
    } finally { setSaving(false); }
  };

  const clone = (a) => aiAssistantAPI.cloneAgent(a.id).then(() => { toast.success('Agent cloned'); load(); });
  const setStatus = (a, status) => aiAssistantAPI.setAgentStatus(a.id, status).then(() => { toast.success(`Agent ${status}`); load(); });
  const remove = (a) => confirmToast({
    message: 'Delete agent?', description: a.name, destructive: true, confirmLabel: 'Delete',
    onConfirm: async () => { await aiAssistantAPI.deleteAgent(a.id); toast.success('Agent deleted'); load(); },
  });

  if (loading) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"><CardSkeleton rows={3} /><CardSkeleton rows={3} /><CardSkeleton rows={3} /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground"><Bot className="h-6 w-6 text-primary" /> AI Agents</h2>
          <p className="text-sm text-muted-foreground">Create and manage multiple branded AI assistants (HR, Payroll, Recruitment…).</p>
        </div>
        <Button onClick={() => setEditing(blank())}><Plus className="mr-2 h-4 w-4" /> New Agent</Button>
      </div>

      {agents.length === 0 ? (
        <EmptyState icon={Bot} title="No agents yet" description="Create your first branded AI assistant." actionLabel="New Agent" onAction={() => setEditing(blank())} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((a) => (
            <Card key={a.id} className="flex flex-col">
              <CardContent className="flex flex-1 flex-col p-5">
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-white" style={{ background: a.theme_color || '#3b4fb8' }}><Bot className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate font-semibold text-foreground">{a.name}</p>
                      {a.is_default && <Star className="h-3.5 w-3.5 fill-warning text-warning" title="Default" />}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{a.provider || 'tenant default'} · {a.model || 'default model'}</p>
                  </div>
                  <Badge variant={STATUS_TONE[a.status] || 'secondary'} className="capitalize">{a.status}</Badge>
                </div>
                <p className="mt-3 line-clamp-2 flex-1 text-sm text-muted-foreground">{a.description || a.welcome_message || 'No description'}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(a.allowed_roles || []).slice(0, 3).map((r) => <Badge key={r} variant="soft" className="text-[10px]">{r.replace('_', ' ')}</Badge>)}
                  {(a.allowed_roles || []).length > 3 && <Badge variant="outline" className="text-[10px]">+{a.allowed_roles.length - 3}</Badge>}
                </div>
                <div className="mt-4 flex items-center gap-1 border-t border-border/60 pt-3">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...a })}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                  <Button size="sm" variant="ghost" title="Clone" onClick={() => clone(a)}><Copy className="h-3.5 w-3.5" /></Button>
                  {a.status === 'active'
                    ? <Button size="sm" variant="ghost" title="Deactivate" onClick={() => setStatus(a, 'inactive')}><Archive className="h-3.5 w-3.5" /></Button>
                    : <Button size="sm" variant="ghost" title="Activate" onClick={() => setStatus(a, 'active')}><CheckCircle2 className="h-3.5 w-3.5 text-success" /></Button>}
                  <Button size="sm" variant="ghost" className="ml-auto text-danger hover:text-danger" title="Delete" onClick={() => remove(a)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size="lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> {editing?.id ? 'Edit Agent' : 'New Agent'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={editing.name} onChange={(e) => set({ name: e.target.value })} placeholder="Payroll Assistant" /></div>
                  <div><Label>Theme Color</Label><div className="flex items-center gap-2"><input type="color" value={editing.theme_color} onChange={(e) => set({ theme_color: e.target.value })} className="h-10 w-12 cursor-pointer rounded border border-border bg-card" /><Input value={editing.theme_color} onChange={(e) => set({ theme_color: e.target.value })} /></div></div>
                </div>
                <div><Label>Description</Label><Input value={editing.description || ''} onChange={(e) => set({ description: e.target.value })} /></div>
                <div><Label>Welcome Message</Label><Textarea rows={2} value={editing.welcome_message || ''} onChange={(e) => set({ welcome_message: e.target.value })} /></div>
                <div><Label>System Prompt</Label><Textarea rows={5} value={editing.system_prompt || ''} onChange={(e) => set({ system_prompt: e.target.value })} placeholder="You are a helpful payroll assistant…" /></div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_default} onChange={(e) => set({ is_default: e.target.checked })} /> Set as default agent</label>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Provider</Label>
                    <Select value={editing.provider || '_inherit'} onValueChange={(v) => { const p = providers.find((x) => x.id === v); set({ provider: v === '_inherit' ? '' : v, model: p?.default_model || editing.model }); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_inherit">Inherit tenant default</SelectItem>
                        {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Model</Label><Input value={editing.model || ''} onChange={(e) => set({ model: e.target.value })} placeholder={activeProvider?.default_model || 'inherit'} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Temperature</Label><Input type="number" step="0.1" min="0" max="2" value={editing.temperature} onChange={(e) => set({ temperature: parseFloat(e.target.value) })} /></div>
                  <div><Label>Max Tokens</Label><Input type="number" value={editing.max_tokens} onChange={(e) => set({ max_tokens: parseInt(e.target.value, 10) })} /></div>
                </div>
                <div><Label className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Allowed Roles</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">{ALL_ROLES.map((r) => <Chip key={r} active={(editing.allowed_roles || []).includes(r)} onClick={() => toggleIn('allowed_roles', r)}>{r.replace('_', ' ')}</Chip>)}</div>
                </div>
                <div><Label>Allowed Modules</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">{ALL_MODULES.map((m) => <Chip key={m} active={(editing.allowed_modules || []).includes(m)} onClick={() => toggleIn('allowed_modules', m)}>{m}</Chip>)}</div>
                </div>
                {/* Preview */}
                <div className="rounded-xl border border-border bg-muted/40 p-3">
                  <p className="mb-2 text-xs font-semibold text-muted-foreground">Preview</p>
                  <div className="flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-lg text-white" style={{ background: editing.theme_color }}><Bot className="h-4 w-4" /></div>
                    <div><p className="text-sm font-semibold text-foreground">{editing.name || 'Agent name'}</p><p className="text-xs text-muted-foreground">{editing.welcome_message || 'Welcome message preview'}</p></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : (editing?.id ? 'Save Agent' : 'Create Agent')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
